/**
 * CFO Cockpit → Balance Sheet. Solvency at a glance from the QBO
 * BalanceSheet report: net assets, working capital, liquidity ratios
 * (with 12-months-ago comparison), the monthly assets / liabilities /
 * equity picture, and the as-of-today account breakdown split into
 * "what we own" vs "what we owe & equity".
 */
import {
  ComposedChart, Bar, Line, XAxis, YAxis, ReferenceLine,
  ResponsiveContainer, Legend, Tooltip as RechartsTooltip,
} from "recharts";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { TabState, Card, KpiStrip, InfoTip } from "./cfoShared";
import { useQboData, QboHeader, fmtGbp, fmtMonthKey } from "./qboShared";

const yoyBadge = (cur, prev) => {
  if (prev == null || !prev || cur == null) return null;
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  return `${pct >= 0 ? "▲" : "▼"} ${Math.abs(pct).toFixed(1)}% vs 12m ago`;
};

const ratioTone = (v, amber, red) =>
  v == null ? "text-slate-900" : v < red ? "text-rose-600" : v < amber ? "text-amber-600" : "text-teal-700";

function SectionList({ sections, side, title, testid }) {
  const list = (sections || []).filter((s) => s.side === side && (s.total !== 0 || s.rows.length));
  return (
    <Card testid={testid}>
      <PanelTitle>{title}</PanelTitle>
      <div className="mt-2 space-y-4">
        {list.map((s) => (
          <div key={s.key} data-testid={`bs-section-${s.key}`}>
            <div className="flex items-baseline justify-between gap-3 border-b border-stone-200 pb-1">
              <span className="text-xs font-semibold text-slate-800">{s.title}</span>
              <span className="text-xs font-mono tabular-nums text-slate-900">{fmtGbp(s.total)}</span>
            </div>
            <ul className="mt-1 space-y-0.5">
              {s.rows.map((r) => (
                <li key={r.name} className="flex items-baseline justify-between gap-3">
                  <span className="text-xs text-stone-500 truncate">{r.name}</span>
                  <span className="text-[11px] font-mono tabular-nums text-stone-600">{fmtGbp(r.value)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {list.length === 0 && <p className="text-xs text-stone-400 py-2">Nothing on this side of the ledger.</p>}
      </div>
    </Card>
  );
}

export default function BalanceSheetTab() {
  const { data, loading, error, refreshing, reload } = useQboData("balance-sheet");
  if (loading || error) {
    return <TabState loading={loading} error={error} testid="bs-state" />;
  }
  const k = data.kpis || {};
  const prior = data.prior || {};
  const months = data.months || [];

  return (
    <div className="flex flex-col gap-6" data-testid="balance-sheet-tab">
      <QboHeader data={data} refreshing={refreshing} onRefresh={reload} testid="bs-refresh-btn" />

      <KpiStrip
        cards={[
          {
            label: "Net assets",
            value: fmtGbpShort(k.net_assets),
            sub: yoyBadge(k.net_assets, prior.net_assets) || "what the company is worth on paper",
            tone: k.net_assets < 0 ? "text-rose-600" : "text-slate-900",
            testid: "bs-kpi-net-assets",
            info: "bs_net_assets",
          },
          {
            label: "Working capital",
            value: fmtGbpShort(k.working_capital),
            sub: yoyBadge(k.working_capital, prior.working_capital) || "current assets − short-term creditors",
            tone: k.working_capital < 0 ? "text-rose-600" : "text-slate-900",
            testid: "bs-kpi-working-capital",
            info: "bs_working_capital",
          },
          {
            label: "Current ratio",
            value: k.current_ratio != null ? `${k.current_ratio}×` : "–",
            sub: k.current_ratio == null ? "nothing material owed short term" : prior.current_ratio != null ? `was ${prior.current_ratio}× 12m ago` : "≥1.5× is comfortable",
            tone: ratioTone(k.current_ratio, 1.5, 1),
            testid: "bs-kpi-current-ratio",
            info: "bs_current_ratio",
          },
          {
            label: "Debt to equity",
            value: k.debt_to_equity != null ? `${k.debt_to_equity}×` : "–",
            sub: prior.debt_to_equity != null ? `was ${prior.debt_to_equity}× 12m ago` : "total liabilities ÷ equity",
            tone: k.debt_to_equity != null && k.debt_to_equity > 1.5 ? "text-rose-600" : "text-slate-900",
            testid: "bs-kpi-gearing",
            info: "bs_gearing",
          },
        ]}
      />

      <KpiStrip
        cards={[
          { label: "Cash at bank", value: fmtGbpShort(k.cash), sub: yoyBadge(k.cash, prior.cash) || "", testid: "bs-kpi-cash" },
          { label: "Owed to us (debtors)", value: fmtGbpShort(k.debtors), sub: yoyBadge(k.debtors, prior.debtors) || "", testid: "bs-kpi-debtors" },
          { label: "Due within 1 year", value: fmtGbpShort(k.creditors_1y), sub: yoyBadge(k.creditors_1y, prior.creditors_1y) || "what we actually owe short term", testid: "bs-kpi-creditors", info: "bs_creditors" },
          {
            label: "Recoverable",
            value: fmtGbpShort(k.recoverable || 0),
            sub: yoyBadge(k.recoverable, prior.recoverable) || "VAT and tax due back, supplier overpayments",
            testid: "bs-kpi-recoverable",
            info: "bs_recoverable",
          },
          {
            label: "Quick ratio",
            value: k.quick_ratio != null ? `${k.quick_ratio}×` : "–",
            sub: k.quick_ratio != null ? "cash + debtors ÷ what we owe short term" : "nothing material owed short term",
            tone: ratioTone(k.quick_ratio, 1, 0.7),
            testid: "bs-kpi-quick",
            info: "bs_quick",
          },
        ]}
      />

      <Card testid="bs-chart-card">
        <PanelTitle>
          Assets vs liabilities vs equity · month-end balances <InfoTip id="bs_chart" />
        </PanelTitle>
        <div className="h-64 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="month" tickFormatter={fmtMonthKey} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={58} />
              <RechartsTooltip
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                labelFormatter={fmtMonthKey}
                formatter={(v, n) => [
                  fmtGbp(v),
                  n === "assets" ? "Total assets" : n === "liabilities" ? "Total liabilities" : "Equity (net assets)",
                ]}
              />
              <Legend
                wrapperStyle={{ fontSize: 10 }}
                formatter={(v) => (v === "assets" ? "Total assets" : v === "liabilities" ? "Total liabilities" : "Equity (net assets)")}
              />
              <ReferenceLine y={0} stroke="#e7e5e4" />
              <Bar dataKey="assets" fill="#1B4F8A" maxBarSize={20} radius={[3, 3, 0, 0]} />
              <Bar dataKey="liabilities" fill="#9b2226" maxBarSize={20} radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="equity" stroke="#0D9488" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card testid="bs-liquidity-card">
        <PanelTitle>
          Liquidity over time <InfoTip id="bs_working_capital" />
        </PanelTitle>
        <div className="h-48 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="month" tickFormatter={fmtMonthKey} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="l" tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={58} />
              <YAxis yAxisId="r" orientation="right" tickFormatter={(v) => `${v}×`} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={38} />
              <RechartsTooltip
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                labelFormatter={fmtMonthKey}
                formatter={(v, n) => (n === "current_ratio" ? [`${v}×`, "Current ratio"] : [fmtGbp(v), "Working capital"])}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} formatter={(v) => (v === "current_ratio" ? "Current ratio (right)" : "Working capital (left)")} />
              <Bar yAxisId="l" dataKey="working_capital" fill="#1B4F8A" fillOpacity={0.35} maxBarSize={20} radius={[3, 3, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="current_ratio" stroke="#d6a419" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionList sections={data.sections} side="own" title="What we own · as of today" testid="bs-own-card" />
        <SectionList sections={data.sections} side="owe" title="What we owe & equity · as of today" testid="bs-owe-card" />
      </div>
    </div>
  );
}
